// Embed session state. Leaf module (no app imports), so tests need no mock.module.
// The session lives only in per-tab sessionStorage, never in the console's localStorage auth.
import { parseHostedKycHosts } from './hosted-kyc-hosts'

export interface REmbedSession {
  session_token: string
  session_id: string
  auth_id: string
  // Sub-account scope ('' for main). A UI hint only: hodor and core enforce access.
  sub_auth_id?: string
  region: string
  permissions: string[]
  // Lets a reload re-pin the postMessage parent without re-minting.
  allowed_origins: string[]
  expires_at: string
  // From session/me; embed's only source for the LiveKit cutoff (it has no profile query).
  org_created_at?: string
  // Whether hodor bound the session to this browser's DPoP key (reported, never inferred).
  dpop_bound?: boolean
}

const STORAGE_KEY_BASE = 'embed_auth'

export const EMBED_INSTANCE_PARAM = '_i'

// Per-iframe instance id that namespaces the session and the DPoP key (sessionStorage is per tab).
// Seeded from the loader's `_i` param and parked on window.name so a navigate-then-reload keeps it.
export const INSTANCE_NAME_PREFIX = 'cx-embed-instance:'

// Pure, so the navigate-then-reload rule is testable. A URL id wins over the frame name.
export const resolveEmbedInstanceId = (search: string, frameName: string): string => {
  const fromUrl = new URLSearchParams(search).get(EMBED_INSTANCE_PARAM) ?? ''
  if (fromUrl) {
    return fromUrl
  }
  const raw = frameName || ''
  return raw.startsWith(INSTANCE_NAME_PREFIX) ? raw.slice(INSTANCE_NAME_PREFIX.length) : ''
}

// Read once at module load, never per call: in-iframe navigation drops the `_i` query.
const INSTANCE_ID = ((): string => {
  try {
    const id = resolveEmbedInstanceId(window.location.search, window.name)
    // The URL wins: a remount for another customer reuses the frame (and window.name) but brings a
    // fresh `_i`, and inheriting the old id would mix tenants.
    const fromUrl = new URLSearchParams(window.location.search).get(EMBED_INSTANCE_PARAM) ?? ''
    if (fromUrl && window.name !== INSTANCE_NAME_PREFIX + fromUrl) {
      window.name = INSTANCE_NAME_PREFIX + fromUrl
    }
    return id
  } catch {
    return ''
  }
})()

// Fails closed: no instance id means no key, and every accessor then refuses storage.
export const embedStorageKey = (instanceId: string): string =>
  instanceId ? `${STORAGE_KEY_BASE}:${instanceId}` : ''

// The hosted page is a top-level tab (one page per tab), so its session needs no per-frame id.
export const HOSTED_INSTANCE_ID = 'hosted-kyc'

const storageKey = (): string => embedStorageKey(isHostedMode() ? HOSTED_INSTANCE_ID : INSTANCE_ID)

// Shared with the DPoP key store so both namespace by the same captured id.
export const embedInstanceId = (): string => INSTANCE_ID

// Fired on an embed 401 (expired overlay + parent notice). Never window.cx.logout: that would
// log out a console tab.
export const EMBED_SESSION_EXPIRED_EVENT = 'embed:session-expired'

// Fired after every setEmbedSession; root providers above the bootstrap re-read the session on it.
export const EMBED_SESSION_READY_EVENT = 'embed:session-ready'

const isUnderPath = (path: string, root: string): boolean =>
  path === root || path.startsWith(`${root}/`)

const HOSTED_KYC_HOSTS = parseHostedKycHosts(process.env.REACT_APP_HOSTED_KYC_HOSTS)

// The authorised signatory's emailed link: a hosted page of its own, on the console host.
export const KYC_SIGNATORY_PATH = '/kyc/signatory'

// /hosted or the signatory link on any host, or every path of a host named in
// REACT_APP_HOSTED_KYC_HOSTS. Same rule as index.html's head guard.
export const isHostedLocation = (
  { hostname, pathname }: { hostname: string; pathname: string },
  hosts: readonly string[] = HOSTED_KYC_HOSTS,
): boolean =>
  hosts.includes(hostname) ||
  isUnderPath(pathname, '/hosted') ||
  isUnderPath(pathname, KYC_SIGNATORY_PATH)

export const isHostedKycHost = (): boolean =>
  typeof window !== 'undefined' && HOSTED_KYC_HOSTS.includes(window.location.hostname)

export const isHostedMode = (): boolean =>
  typeof window !== 'undefined' && isHostedLocation(window.location)

// A route property. Segment match, so a route like /embeddings is not embed mode. Hosted mode
// counts: it runs on an embed session, so every embed ban and gate applies.
export const isEmbedMode = (): boolean =>
  (typeof window !== 'undefined' && isUnderPath(window.location.pathname, '/embed')) ||
  isHostedMode()

export const getEmbedSession = (): REmbedSession | null => {
  try {
    const key = storageKey()
    if (!key) {
      return null
    }
    const raw = window.sessionStorage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as REmbedSession
    // Shape-validated once here. session_id is a security check: it scopes playground run adoption,
    // so a record without it must not read as a working session.
    if (
      typeof parsed?.session_token !== 'string' ||
      parsed.session_token === '' ||
      typeof parsed.session_id !== 'string' ||
      parsed.session_id === '' ||
      !Array.isArray(parsed.allowed_origins)
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

// hodor refuses org-wide routes (queues, teams, hours, skills, channel config, voice providers,
// clone) to sub-account sessions; callers skip them and hide what depends on them. A UI hint only.
export const isSubAccountEmbedSession = (): boolean =>
  isEmbedMode() && !!getEmbedSession()?.sub_auth_id

// Returns whether the session reads back; false means blocked storage, which must be surfaced.
export const setEmbedSession = (session: REmbedSession): boolean => {
  let persisted = false
  try {
    const key = storageKey()
    // No instance id: refuse via the catch, so the ready event below still fires.
    if (!key) {
      throw new Error('embed: refusing to persist a session without an instance id')
    }
    const payload = JSON.stringify(session)
    // Clear first so a discarded write cannot leave the previous session readable.
    window.sessionStorage.removeItem(key)
    window.sessionStorage.setItem(key, payload)
    // Compare with the payload, not presence: a blocked store may accept and silently discard.
    persisted = window.sessionStorage.getItem(key) === payload
  } catch {
    persisted = false
  }
  // Fire even on failure: a missed event strands root providers.
  window.dispatchEvent(new Event(EMBED_SESSION_READY_EVENT))
  return persisted
}

export const clearEmbedSession = (): void => {
  try {
    const key = storageKey()
    if (key) {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // Best-effort.
  }
}

// Rewrites the first /v{n}/ to /v{n}/embed/ so calls hit hodor's embed gateway (allow-list and
// tenant are enforced there). Identity outside embed; idempotent.
export const toEmbedPath = (url: string): string => {
  if (!isEmbedMode() || !url || /\/v\d+\/embed\//.test(url)) {
    return url
  }
  return url.replace(/\/(v\d+)\//, '/$1/embed/')
}

export const dispatchEmbedSessionExpired = (): void => {
  window.dispatchEvent(new Event(EMBED_SESSION_EXPIRED_EVENT))
}

// Router-path twin of toEmbedPath: keeps in-iframe navigation under /embed, since a bare path
// renders the console login in the iframe. Segment test, so /embeddings is not 'prefixed'.
export const toEmbedRoutePath = (path: string): string =>
  isEmbedMode() && !isHostedMode() && !/^\/embed(\/|$)/.test(path) ? `/embed${path}` : path
